declare const bootstrap: any;
let modal: HTMLDivElement | null = null;
const modalQueue: Array<() => void> = [];
let isModalOpen = false;

function runNextInQueue(): void {
  isModalOpen = false;
  if (modalQueue.length > 0) {
    isModalOpen = true;
    const next = modalQueue.shift()!;
    next();
    if (modal) modal.addEventListener('hidden.bs.modal', runNextInQueue, { once: true });
  }
}

export function enqueueModal(fn: () => void): void {
  if (isModalOpen) {
    modalQueue.push(fn);
    return;
  }
  isModalOpen = true;
  fn();
  if (modal) modal.addEventListener('hidden.bs.modal', runNextInQueue, { once: true });
}

export enum MessageType {
  Primary,
  Secondary,
  Success,
  Danger,
  Warning,
  Info,
  Light,
  Dark,
  None
}

export default function showModal(title: string | null, message: string, type: MessageType = MessageType.None, modalClasses: string[] | null = null): void {

  let id = crypto.randomUUID();
  if (!document.getElementById(id)) {
    let modalDialog, modalHeader, modalContent, modalBody, modalFooter, closeButton;
    // Create modal element if it doesn't exist
    modal = document.createElement('div');
    modal.classList.add('modal', 'fade');
    modal.setAttribute('tabindex', '-1');
    modal.setAttribute('role', 'dialog');
    modal.id = id;

      if (modalClasses != null) {
        modal.classList.add(...modalClasses);
      }

    // Create modal dialog
    modalDialog = document.createElement('div');
    modalDialog.classList.add('modal-dialog', 'modal-dialog-centered');
    modalDialog.setAttribute('role', 'document');

    // Create modal content
    modalContent = document.createElement('div');
    modalContent.classList.add('modal-content', 'mx-5');

    if (title == null) {
      switch (type) {
        case MessageType.Success || MessageType.Success.toString:
          title = "✅ Done";
          type = 2;
          break;
        case MessageType.Info:
          title = "ℹ️ Info";
          type = 5;
          break;
        case MessageType.Warning || "Warning":
          title = "⚠️ Warning";
          type = 4;
          break;
        case MessageType.Danger || "Danger":
          title = "❌ Error";
          type = 3;
      }
    }
    // Create modal header if title is not null
    if (title !== null) {
      modalHeader = document.createElement('div');
      modalHeader.classList.add('modal-header');
      const modalTitle = document.createElement('h5');
      modalTitle.classList.add('modal-title');

      modalTitle.innerHTML = title;
      modalHeader.appendChild(modalTitle);
      modalContent.appendChild(modalHeader);
    }

    if (type != MessageType.None) {

      let typeClass = MessageType[type].toLowerCase();
      if (modalHeader != null) {
        modalHeader.classList.add(`text-${typeClass}`);
      }
      modalContent.classList.add(`border-${typeClass}`);
    }

    // Create modal body
    modalBody = document.createElement('div');
    modalBody.classList.add('modal-body');
    modalContent.appendChild(modalBody);

    // Create modal footer
    modalFooter = document.createElement('div');
    modalFooter.classList.add('modal-footer');

    closeButton = document.createElement('button');
    closeButton.classList.add('btn', 'btn-outline-secondary', 'border-0');
    closeButton.setAttribute('type', 'button');
    closeButton.setAttribute('data-bs-dismiss', 'modal');
    closeButton.textContent = 'Close';
    modalFooter.appendChild(closeButton);
    modalContent.appendChild(modalFooter);

    // Assemble modal elements
    modalDialog.appendChild(modalContent);
    modal.appendChild(modalDialog);

    // Append modal to body
    document.body.appendChild(modal);
  }

  // Update modal body with new message
  const modalBody = modal.querySelector('.modal-body');
  if (modalBody) {
    modalBody.innerHTML = message;
  }

  // Show modal
  if (typeof bootstrap !== 'undefined') {
    const bootstrapModal = new bootstrap.Modal(modal);
    bootstrapModal.show();
  }

  let resendActivationEmail = modal.querySelector('.resend-activation-email') as HTMLAnchorElement;
  if (resendActivationEmail) {
    resendActivationEmail.addEventListener('click', function (e) {
      e.preventDefault();
      resendActivationEmail.classList.add("disabled");
      fetch('/Account/Home/ResendConfirmationEmailAsync', { method: "POST" }).then(response => response.text()).then((data) => {
        let result = document.createElement("div");
        result.classList.add("fw-bold", "text-success");
        result.innerHTML = data;
        resendActivationEmail.parentElement.append(result);
      }).catch(e => {
        resendActivationEmail.classList.remove("disabled");
        resendActivationEmail.innerHTML = "Failed to send email. Please try again.";
      });
    });
  }
}

export function showAlertModals() {
  let modalAlerts = document.querySelectorAll('.modal-alert');

  modalAlerts.forEach((modalAlert) => {
    const message = modalAlert.getAttribute('data-modal-message');
    const type = modalAlert.getAttribute('data-modal-message-type');
    enqueueModal(() => showModal(null, message, MessageType[type]));
  });
}

export function maybeShowInstagramModal(): void {
  const allowedPaths = ['/', '/jobs', '/jobseekers', '/job']; // extend as needed
  const path = window.location.pathname.toLowerCase();

  const isOnAllowedPage = allowedPaths.some(p => path == p);
  const isCorrectDay = new Date().getDay() === 4; //Tuesday
  const alreadyDismissed = localStorage.getItem('instagramModalDismissed');
  const showChance = Math.random() < 0.3; // 20% chance

  if (!isOnAllowedPage || !isCorrectDay || alreadyDismissed || !showChance) return;

  const message = `
    <p>✅ We post daily top jobs from YardandGroom, plus the occasional cheeky equestrian meme 😎</p>
    <p>🐴 Got a great horsey moment to share? Tag @YardandGroom — we love reposting our favourites!</p>
    <a href="https://www.instagram.com/yardandgroom" target="_blank" class="btn btn-primary mt-2">
      Join the Herd on Instagram <i class='fab fa-instagram'></i>
    </a>
  `;

  showModal("You + Us + Horses = Insta Goals", message, MessageType.Info);
  localStorage.setItem('instagramModalDismissed', 'true');
}

export function maybeShowMobileAppModal(): void {
  const settings = (window as any).appSettings ?? {};
  const APP_STORE_URL = settings.iosAppUrl as string;
  const PLAY_STORE_URL = settings.androidAppUrl as string;
  if (!APP_STORE_URL && !PLAY_STORE_URL) return;

  const allowedPaths = ['/', '/jobs', '/jobseekers', '/account'];
  const path = window.location.pathname.toLowerCase();
  const isOnAllowedPage = allowedPaths.some(p => path === p);
  const dismissedKey = 'mobileAppModalDismissed';

  if (!isOnAllowedPage || localStorage.getItem(dismissedKey)) return;

  const message = `
    <button type="button" class="btn-close position-absolute top-0 end-0 m-2" style="z-index:1;" data-bs-dismiss="modal" aria-label="Close"></button>
    <img src="/content/images/mobile/site-modal.webp" class="mobile-modal-hero w-100 d-block" alt="YardandGroom Mobile App" />
    <div class="text-center py-3 px-3">
      <p class="text-muted small mb-2">Sign in on the app and get<br/><strong class="text-success">10% off a feature/spotlight/subscription</strong>,<br /> automatically applied at checkout!</p>
      <div class="d-flex justify-content-center align-items-center gap-3 flex-wrap">
        <a href="${APP_STORE_URL}" target="_blank" rel="noopener">
          <img src="/content/images/mobile/ios-button.png" alt="Download on the App Store" class="mobile-store-btn" />
        </a>
        <a href="${PLAY_STORE_URL}" target="_blank" rel="noopener">
          <img src="/content/images/mobile/googleplay-button.png" alt="Get it on Google Play" class="mobile-store-btn" />
        </a>
      </div>
    </div>
  `;

  enqueueModal(() => {
    showModal(null, message, MessageType.None, ["modal-lg"]);

    if (!document.getElementById('mobile-app-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'mobile-app-modal-styles';
      style.innerHTML = `
        .mobile-app-modal-wrap .modal-body { padding: 0; }
        .mobile-modal-hero {object-fit: cover; }
        .mobile-store-btn { height: 44px; width: auto; }
      `;
      document.head.appendChild(style);
    }

    try {
      if (modal) {
        modal.classList.add('mobile-app-modal-wrap');

        const modalFooter = modal.querySelector('.modal-footer') as HTMLElement | null;
        if (modalFooter) {
          modalFooter.innerHTML = '';
          const dontShowBtn = document.createElement('button');
          dontShowBtn.type = 'button';
          dontShowBtn.className = 'btn btn-link text-muted small p-0';
          dontShowBtn.setAttribute('data-bs-dismiss', 'modal');
          dontShowBtn.textContent = "Don't show again";
          dontShowBtn.addEventListener('click', () => {
            try { localStorage.setItem(dismissedKey, 'true'); } catch (e) {}
          });
          modalFooter.appendChild(dontShowBtn);
        }
      }
    } catch (e) {
      // non-critical
    }
  });
}

export function maybeShowMerchModal(): void {
  const allowedPaths = ['/']; // only show on homepage
  const path = window.location.pathname.toLowerCase();

  const isOnAllowedPage = allowedPaths.some(p => path == p);
  const shownCountKey = 'merchModalShownCount';
  const shownCount = parseInt(localStorage.getItem(shownCountKey) || '0', 10);
  // Don't show after (or on) June 1, 2026
  const cutoff = new Date(2026, 5, 1); // months are 0-based, so 5 = June
  if (new Date() >= cutoff) return;

  // don't show during the first 4 days of any month
  const today = new Date();
  const dayOfMonth = today.getDate();
  if (dayOfMonth <= 4) return;

  // don't show if not on allowed page or already shown 3 times
  if (!isOnAllowedPage || shownCount >= 3) return;

  const shopUrl = 'https://shop.yardandgroom.com'; // update to your shop URL
  const imageUrl = '/content/images/shop/navy-cap.webp'; // update to your merch image path

  const message = `
    <button type="button" class="btn-close position-absolute top-0 end-0 m-3" data-bs-dismiss="modal" aria-label="Close"></button>
    <div class="text-center py-2">
      <a href="${shopUrl}" target="_blank" rel="noopener" class="d-block merch-visit-link">
        <img src="${imageUrl}" class="img-fluid rounded mx-auto d-block merch-image" alt="YardandGroom Merch - Navy Cap" />
      </a>

      <h2 class="h3 fw-bold mt-3 mb-1 merch-title">Introducing the YardandGroom Shop</h2>
      <p class="text-muted mb-3 merch-subtitle">Extremely limited merch, thoughtfully designed in Wiltshire, UK. <br />Use code <strong>yag26</strong> for <strong>10% off!</strong></p>

      <a href="${shopUrl}" target="_blank" rel="noopener" class="btn btn-dark rounded-pill merch-cta merch-visit-link">Visit store</a>
    </div>
  `;

  // Use primary bootstrap look but keep markup simple
  showModal(null, message);

  // increment shown count and persist
  try {
    const newCount = shownCount + 1;
    localStorage.setItem(shownCountKey, String(newCount));
  }
  catch (e) {
    // ignore storage errors
  }

  // Minimal custom styles appended once
  if (!document.getElementById('merch-modal-styles')) {
    const style = document.createElement('style');
    style.id = 'merch-modal-styles';
    style.innerHTML = `
      .modal-dialog { max-width: 680px; }
      .merch-image { max-width: 250px; border-radius: 12px; }
      .merch-title { font-size: 1.75rem; }
      .modal-footer { display: none; }
      .btn-close { box-shadow: 0 6px 18px rgba(0,0,0,0.08); }
      @media (max-width: 576px) { .modal-dialog { max-width: 92%; } .merch-title { font-size: 1.25rem; } }
    `;
    document.head.appendChild(style);
  }

  // Apply merch-modal class to modal content and attach visit listener
  try {
    const merchModal = modal as HTMLDivElement | null;
    if (merchModal) {
      const modalContent = merchModal.querySelector('.modal-content');
      if (modalContent) modalContent.classList.add('merch-modal');

      // no longer setting a separate dismissed flag; showing is tracked by shown count only
    }
  }
  catch (e) {
    // non-critical
  }
}
